import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { Paper } from '@mui/material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import AnimationPage from '../../../pages/AnimationPage';
import ServiceZonePanel from './ServiceZonePanel';
import RoomDetailPanel from './RoomDetailPanel';
import IotSetupPanel from './IotSetupPanel';
import CategoryCardDialog, { AddCategoryDialog } from './CategoryCardDialog';
import ConfirmDialog from './ConfirmDialog';
import { sendWithOfflineConfirm, OFFLINE_SEND_MESSAGE } from './iotOffline';
import LightNameEditModal from './LightNameEditModal';
import CCTVIcon from '../../../_icons/CCTVIcon';
import FacilityIcon from '../../../_icons/FacilityIcon';
import {
  getBranchIot,
  getIotSchedule,
  getIotSensor,
  getIotRoom,
  getIotSensorStatus,
  controlDoor,
  controlLight,
  controlLightChannels,
  getIotSensorRelays,
  saveIotSensorRelays,
} from '../../../api/iot';
import {
  ALL_CHANNELS,
  CATEGORY_CARDS,
  cardOf,
  relayCategoryForCard,
  buildChannelMap,
  enabledChannels,
  enabledChannelsOfCard,
  isOn,
} from './iotChannels';
import { useToast } from 'use-toast-mui';
import { errorMessages } from '../../../utils/methods';
import {
  PageContainer,
  PageTitle,
  BreadCrumb,
  HeaderTabsContainer,
  TabGroup,
  Tab,
  CenterTabGroup,
  CenterTab,
  FilterTabsContainer,
  FilterTabGroup,
  FilterTab,
  EntryTimeSettingButton,
  ContentContainer,
} from './FacilityControlStyled';

// 일시개방 성공 후 "개방됨" 표시 유지 시간
const DOOR_OPENED_FEEDBACK_MS = 3000;
// 제어 후 Elyxion 반영을 기다렸다가 상태 재조회
const STATUS_REFETCH_DELAY_MS = 2000;
// 좌측 룸 목록은 여러 룸이라 주기적으로 갱신
const STATUS_POLL_MS = 30000;

const FacilityControl = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { user } = useSelector(state => state.user);
  const branchId = user?.branch?.branchId;
  const token = user?.token;
  const isSuperAdmin = user?.userRole === 'ROLE_SUPERADMIN';

  const [mainTab, setMainTab] = useState('lessonRoom'); // lessonRoom, otherFacility
  const [centerTab, setCenterTab] = useState('facilityControl'); // facilityControl, cctv
  const [filterTab, setFilterTab] = useState('all'); // all, serviceZone, privateZone
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [isEntryTimeModalOpen, setIsEntryTimeModalOpen] = useState(false);

  // 룸 목록 조회
  const { data: iotRoomData, isLoading: isRoomLoading, isError: isRoomError } = useQuery({
    queryKey: ['iotRoom', branchId],
    queryFn: () => getIotRoom(branchId, token),
    enabled: !!branchId && !!token,
    retry: false,
  });

  // 룸 목록 데이터 설정
  useEffect(() => {
    if (isRoomError) {
      // 연동 해제 등으로 404 — 이전 룸 목록을 비운다
      setRooms([]);
      setSelectedRoom(null);
      return;
    }
    if (iotRoomData) {
      // API 데이터를 rooms state에 설정
      const roomList = Array.isArray(iotRoomData) ? iotRoomData : [];
      setRooms(roomList);
      // 선택 룸은 최신 객체로 바꿔 끼운다(도어 인증 시간 저장 등 갱신 반영). 없으면 첫 룸
      setSelectedRoom(prev => (prev && roomList.find(r => r.id === prev.id)) || roomList[0] || null);
    }
  }, [iotRoomData, isRoomError]);

  // 지점 IoT 매핑 정보 조회
  const { data: branchIotData, isLoading: isBranchIotLoading } = useQuery({
    queryKey: ['branchIot', branchId],
    queryFn: () => getBranchIot(branchId, token),
    enabled: !!branchId && !!token,
    retry: false, // 미연동 지점은 404 — 재시도 없이 바로 연동 패널을 보여준다
  });

  // 선택된 룸의 id 가져오기 (API에서는 roomId로 사용)
  const selectedRoomId = selectedRoom?.id;

  // 전원 스케쥴 조회
  const { data: iotScheduleData, isLoading: isScheduleLoading } = useQuery({
    queryKey: ['iotSchedule', branchId, selectedRoomId],
    queryFn: () => getIotSchedule(branchId, selectedRoomId, token),
    enabled: !!branchId && !!selectedRoomId && !!token,
  });

  // IoT 센서 동기화 조회
  const { data: iotSensorData, isLoading: isSensorLoading } = useQuery({
    queryKey: ['iotSensor', branchId],
    queryFn: () => getIotSensor(branchId, token),
    enabled: !!branchId && !!token,
    retry: false,
    refetchInterval: STATUS_POLL_MS, // 지정 시각까지 열림이 지나면 서버가 N 으로 정리 — 화면도 따라가게
  });

  // 선택된 룸의 sensorId 가져오기
  const selectedSensorId = selectedRoom?.sensorId;

  // 룸별 센서 상태 (l1~l8 onOff) — 좌측 타일과 우측 전원 카드가 같이 쓴다
  const sensorRooms = rooms.filter(r => r.sensorId);
  const statusQueries = useQueries({
    queries: sensorRooms.map(room => ({
      queryKey: ['iotSensorStatus', room.sensorId],
      queryFn: () => getIotSensorStatus(room.sensorId, token),
      enabled: !!token,
      refetchInterval: STATUS_POLL_MS,
    })),
  });
  const statusByRoom = {};
  const sensorByRoom = {};
  const sensorBySensorId = Object.fromEntries((iotSensorData || []).map(sensor => [sensor.sensorId, sensor]));
  sensorRooms.forEach((room, i) => {
    statusByRoom[room.id] = statusQueries[i]?.data?.channels;
    const sensor = sensorBySensorId[room.sensorId];
    sensorByRoom[room.id] = {
      online: sensor?.connectYn === 'Y',
      alwaysOpen: sensor?.doorAlwaysOpenYn === 'Y',
      // "2026-09-17 21:00:00" → "21:00"
      openUntil: sensor?.doorOpenUntil ? String(sensor.doorOpenUntil).split(/[T ]/)[1]?.slice(0, 5) : null,
    };
  });
  const sensorStatusData = selectedSensorId ? statusQueries[sensorRooms.findIndex(r => r.sensorId === selectedSensorId)]?.data : null;

  // 룸별 릴레이 (이름·사용여부·카테고리) — 카드 구성에 필요
  const relayQueries = useQueries({
    queries: sensorRooms.map(room => {
      const dbId = sensorBySensorId[room.sensorId]?.id;
      return {
        queryKey: ['iotSensorRelays', dbId],
        queryFn: () => getIotSensorRelays(dbId, token),
        enabled: !!dbId && !!token,
      };
    }),
  });
  const relaysByRoom = {};
  sensorRooms.forEach((room, i) => {
    const data = relayQueries[i]?.data;
    relaysByRoom[room.id] = Array.isArray(data) ? data : [];
  });
  // 방금 추가해 아직 채널이 없는 카드 {roomId: ['LIGHT']}
  const [extraCardsByRoom, setExtraCardsByRoom] = useState({});

  const refetchStatusLater = sensorId => {
    setTimeout(() => queryClient.invalidateQueries({ queryKey: ['iotSensorStatus', sensorId] }), STATUS_REFETCH_DELAY_MS);
  };

  // 룸별 일시개방 진행 상태 {roomId: 'pending' | 'opened'}
  const [doorStatus, setDoorStatus] = useState({});
  const onControlError = error => !error?.silent && toast.error(errorMessages(error) || t('전원 제어에 실패했습니다'));
  const channelsMutation = useMutation({
    mutationFn: data => sendWithOfflineConfirm(force => controlLightChannels(data, token, force), t(OFFLINE_SEND_MESSAGE)),
    onError: onControlError,
  });
  const singleMutation = useMutation({
    mutationFn: data => sendWithOfflineConfirm(force => controlLight(data, token, force), t(OFFLINE_SEND_MESSAGE)),
    onError: onControlError,
  });

  const runControl = (room, mutation, payload) => {
    mutation.mutate(payload, { onSettled: () => refetchStatusLater(room.sensorId) });
  };
  const controlPending = channelsMutation.isPending || singleMutation.isPending;

  // 열린 카드 다이얼로그 {room, cardKey: 'POWER' | 카테고리 키}
  const [cardDialog, setCardDialog] = useState(null);
  const dialogRoom = cardDialog?.room;
  const dialogRelays = dialogRoom ? relaysByRoom[dialogRoom.id] : [];
  const dialogChannels = dialogRoom ? statusByRoom[dialogRoom.id] : null;
  const dialogSensorDbId = dialogRoom ? sensorBySensorId[dialogRoom.sensorId]?.id : null;
  const dialogMap = buildChannelMap(dialogRelays);
  const isPowerDialog = cardDialog?.cardKey === 'POWER';
  const dialogCats = cardDialog && !isPowerDialog ? cardOf(cardDialog.cardKey)?.relayCategories || [] : [];
  const cardLabelOfCategory = category => CATEGORY_CARDS.find(c => c.relayCategories.includes(category))?.label;
  const rowOf = ch => ({ channel: ch, label: dialogMap[ch].name, on: isOn(dialogChannels, ch), disabled: !dialogMap[ch].enabled });
  const dialogRows = cardDialog
    ? ALL_CHANNELS.filter(ch => (isPowerDialog ? !dialogMap[ch].category : dialogCats.includes(dialogMap[ch].category))).map(rowOf)
    : [];
  const dialogCandidates =
    cardDialog && !isPowerDialog
      ? ALL_CHANNELS.filter(ch => !dialogCats.includes(dialogMap[ch].category)).map(ch => ({
          channel: ch,
          label: dialogMap[ch].name,
          otherCard: dialogMap[ch].category ? t(cardLabelOfCategory(dialogMap[ch].category) || '') : null,
        }))
      : [];
  const dialogControlChannels = cardDialog
    ? isPowerDialog
      ? enabledChannels(dialogRelays)
      : enabledChannelsOfCard(dialogRelays, cardDialog.cardKey)
    : [];

  // 릴레이 저장 (이름/사용여부/카테고리). 요청에 실린 sensorId 로 갱신해 다이얼로그를 닫은 뒤에도 반영되게
  const [editingLight, setEditingLight] = useState(null);
  const saveRelaysMutation = useMutation({
    mutationFn: data => saveIotSensorRelays(data, token),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['iotSensorRelays', variables.sensorId] });
      setEditingLight(null);
    },
    onError: error => toast.error(errorMessages(error) || t('전원 설정 저장에 실패했습니다')),
  });
  const relaySetting = (channel, category) => {
    const relay = dialogRelays.find(r => r.relayChannel === channel);
    return { relayChannel: channel, relayName: relay?.relayName || `전원 ${channel}`, useYn: relay?.useYn || 'Y', category };
  };
  const handleRemoveFromCard = channel =>
    dialogSensorDbId && saveRelaysMutation.mutate({ sensorId: dialogSensorDbId, relays: [relaySetting(channel, '')] });
  const handleAddChannels = channels =>
    dialogSensorDbId &&
    channels.length > 0 &&
    saveRelaysMutation.mutate({
      sensorId: dialogSensorDbId,
      relays: channels.map(ch => relaySetting(ch, relayCategoryForCard(cardDialog.cardKey))),
    });
  // 카테고리 제거는 확인 모달을 거친다 {room, cardKey, sensorDbId, channels, relays}
  const [removeCardConfirm, setRemoveCardConfirm] = useState(null);
  const handleRemoveCard = () => {
    if (!cardDialog || isPowerDialog) return;
    setRemoveCardConfirm({
      room: cardDialog.room,
      cardKey: cardDialog.cardKey,
      sensorDbId: dialogSensorDbId,
      channels: dialogRows.map(r => r.channel),
      relays: dialogRelays,
    });
  };
  const confirmRemoveCard = () => {
    const target = removeCardConfirm;
    if (!target) return;
    setExtraCardsByRoom(prev => ({ ...prev, [target.room.id]: (prev[target.room.id] || []).filter(k => k !== target.cardKey) }));
    if (target.channels.length > 0 && target.sensorDbId) {
      const settings = target.channels.map(ch => {
        const relay = target.relays.find(r => r.relayChannel === ch);
        return { relayChannel: ch, relayName: relay?.relayName || `전원 ${ch}`, useYn: relay?.useYn || 'Y', category: '' };
      });
      saveRelaysMutation.mutate({ sensorId: target.sensorDbId, relays: settings });
    }
    setRemoveCardConfirm(null);
    setCardDialog(null);
  };
  const handleEditRelay = channel => {
    const relay = dialogRelays.find(r => r.relayChannel === channel);
    setEditingLight({ relayChannel: channel, relayName: relay?.relayName || '', useYn: relay?.useYn || 'Y', category: relay?.category || '' });
  };

  // 카테고리 추가 다이얼로그 {room, existing}
  const [addDialog, setAddDialog] = useState(null);
  const handleSelectCategory = key => {
    const room = addDialog.room;
    setExtraCardsByRoom(prev => ({ ...prev, [room.id]: [...new Set([...(prev[room.id] || []), key])] }));
    setAddDialog(null);
    setCardDialog({ room, cardKey: key });
  };

  // 일시개방: 누를 때마다 열림 명령 (oneshot, 몇 초 뒤 자동 닫힘). 상태 토글 없음
  const handleOpenDoor = async room => {
    if (!room?.sensorId) {
      toast.error(t('센서 정보를 찾을 수 없습니다'));
      return;
    }
    setDoorStatus(prev => ({ ...prev, [room.id]: 'pending' }));
    try {
      await sendWithOfflineConfirm(
        force => controlDoor({ sensorId: room.sensorId, onOff: 'on', type: 'oneshot', mode: 'auto' }, token, force),
        t(OFFLINE_SEND_MESSAGE),
      );
      setDoorStatus(prev => ({ ...prev, [room.id]: 'opened' }));
      setTimeout(() => setDoorStatus(prev => ({ ...prev, [room.id]: undefined })), DOOR_OPENED_FEEDBACK_MS);
    } catch (error) {
      setDoorStatus(prev => ({ ...prev, [room.id]: undefined }));
      if (!error?.silent) toast.error(errorMessages(error) || t('일시개방 실행에 실패했습니다'));
    }
  };

  // 필터링된 룸 목록
  const filteredRooms = rooms.filter(room => {
    if (filterTab === 'all') return true;
    if (filterTab === 'serviceZone') return room.zoneType === 'SERVICE';
    if (filterTab === 'privateZone') return room.zoneType === 'PRIVATE';
    return true;
  });

  return (
    <AnimationPage>
      <PageContainer component={Paper} elevation={2}>
        <PageTitle>{t('IoT 제어 시스템')}</PageTitle>
        <BreadCrumb>
          {t('IoT 제어 시스템')} &gt; {t('시설제어')}
        </BreadCrumb>

        {isSuperAdmin && !isBranchIotLoading && (
          <IotSetupPanel
            branchId={branchId}
            token={token}
            branchIot={branchIotData}
            rooms={rooms}
            sensors={iotSensorData}
          />
        )}

        {/* 상단 탭 */}
        {/* <HeaderTabsContainer>
          <TabGroup>
            <Tab isActive={mainTab === 'lessonRoom'} onClick={() => setMainTab('lessonRoom')}>
              {t('레슨룸')}
            </Tab>
            <Tab isActive={mainTab === 'otherFacility'} onClick={() => setMainTab('otherFacility')}>
              {t('기타시설')}
            </Tab>
          </TabGroup>

          <CenterTabGroup>
            <CenterTab isActive={centerTab === 'facilityControl'} onClick={() => setCenterTab('facilityControl')}>
              <FacilityIcon fontSize="small" />
              {t('시설 제어')}
            </CenterTab>
            <CenterTab isActive={centerTab === 'cctv'} onClick={() => setCenterTab('cctv')}>
              <CCTVIcon width={20} height={20} color={centerTab === 'cctv' ? '#fff' : '#666'} />
              {t('CCTV')}
            </CenterTab>
          </CenterTabGroup>
        </HeaderTabsContainer> */}

        {/* 필터 탭 */}
        {/* <FilterTabsContainer>
          <FilterTabGroup>
            <FilterTab isActive={filterTab === 'all'} onClick={() => setFilterTab('all')}>
              {t('전체')}
            </FilterTab>
            <FilterTab isActive={filterTab === 'serviceZone'} onClick={() => setFilterTab('serviceZone')}>
              {t('서비스존')}
            </FilterTab>
            <FilterTab isActive={filterTab === 'privateZone'} onClick={() => setFilterTab('privateZone')}>
              {t('프라이빗존')}
            </FilterTab>
          </FilterTabGroup>
          <EntryTimeSettingButton onClick={() => setIsEntryTimeModalOpen(true)}>
            <AccessTimeOutlinedIcon sx={{ fontSize: 24 }} />
            {t('입장시간 설정')}
          </EntryTimeSettingButton>
        </FilterTabsContainer> */}

        {/* 컨텐츠 영역 */}
        {centerTab === 'facilityControl' && (
          <ContentContainer>
            <ServiceZonePanel
              rooms={filteredRooms}
              selectedRoom={selectedRoom}
              onSelectRoom={setSelectedRoom}
              statusByRoom={statusByRoom}
              relaysByRoom={relaysByRoom}
              extraCardsByRoom={extraCardsByRoom}
              sensorByRoom={sensorByRoom}
              doorStatus={doorStatus}
              onOpenDoor={handleOpenDoor}
              onOpenCard={(room, cardKey) => setCardDialog({ room, cardKey })}
              onAddCard={(room, existing) => setAddDialog({ room, existing })}
            />
            <RoomDetailPanel
              room={selectedRoom}
              relays={selectedRoom ? relaysByRoom[selectedRoom.id] : []}
              sensorData={iotSensorData}
              sensorStatusData={sensorStatusData}
              selectedSensorId={selectedSensorId}
              scheduleData={iotScheduleData}
            />
          </ContentContainer>
        )}

        {/* 전원/카테고리 카드 다이얼로그 */}
        <CategoryCardDialog
          open={!!cardDialog}
          onClose={() => setCardDialog(null)}
          cardKey={cardDialog?.cardKey}
          roomName={dialogRoom?.roomNm}
          rows={dialogRows}
          candidates={dialogCandidates}
          pending={controlPending || saveRelaysMutation.isPending}
          onToggleAll={nextOn =>
            dialogRoom && runControl(dialogRoom, channelsMutation, { sensorId: dialogRoom.sensorId, channels: dialogControlChannels, onOff: nextOn ? 'on' : 'off' })
          }
          onToggleRow={(channel, nextOn) =>
            dialogRoom && runControl(dialogRoom, singleMutation, { sensorId: dialogRoom.sensorId, light: `l${channel}`, onOff: nextOn ? 'on' : 'off' })
          }
          onEditRow={handleEditRelay}
          onRemoveRow={handleRemoveFromCard}
          onAddChannels={handleAddChannels}
          onRemoveCard={handleRemoveCard}
        />
        <ConfirmDialog
          open={!!removeCardConfirm}
          title={`${t(cardOf(removeCardConfirm?.cardKey)?.label || '')} ${t('카테고리를 제거할까요?')}`}
          message={
            removeCardConfirm?.channels.length
              ? `${t('전원')} ${removeCardConfirm.channels.length}${t('개는 전원 카드로 돌아갑니다.')}`
              : t('아직 넣은 전원이 없습니다.')
          }
          confirmText={t('제거')}
          danger
          isPending={saveRelaysMutation.isPending}
          onCancel={() => setRemoveCardConfirm(null)}
          onConfirm={confirmRemoveCard}
        />
        <AddCategoryDialog
          open={!!addDialog}
          onClose={() => setAddDialog(null)}
          roomName={addDialog?.room.roomNm}
          existing={addDialog?.existing || []}
          onSelect={handleSelectCategory}
        />
        <LightNameEditModal
          open={!!editingLight}
          onClose={() => setEditingLight(null)}
          onSave={data => dialogSensorDbId && saveRelaysMutation.mutate({ sensorId: dialogSensorDbId, relays: [data] })}
          lightData={editingLight}
          isLoading={saveRelaysMutation.isPending}
        />

        {centerTab === 'cctv' && (
          <ContentContainer>
            {/* CCTV 컨텐츠 - 추후 구현 */}
            <div style={{ textAlign: 'center', padding: '100px', color: '#666' }}>
              {t('CCTV 기능은 추후 구현 예정입니다.')}
            </div>
          </ContentContainer>
        )}
      </PageContainer>
    </AnimationPage>
  );
};

export default FacilityControl;
